import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import shopApi, { detectCountry } from '../services/shopApi';
import ProductCard from '../components/ProductCard';
import './HomePage.css';
import { AuthenticityGuide, AppDownloadSection } from './CheckoutPage';

const CAT_ICONS = {
  Medicine:        '💊',
  Vitamins:        '🫐',
  Fashion:         '👗',
  Beauty:          '💄',
  Food:            '🍱',
  Footwear:        '👟',
  'Luxury Watches':'⌚',
  'Luxury Bags':   '👜',
  Skincare:        '🧴',
  Alcohol:         '🥃',
  Electronics:     '🔌',
  default:         '📦',
};

/* ── Blog category color map ── */
const BLOG_CAT_COLORS = {
  'Authenticity':  { bg: 'rgba(0,229,204,0.08)',   border: 'rgba(0,229,204,0.2)',   text: '#00E5CC' },
  'Product News':  { bg: 'rgba(34,217,127,0.08)',  border: 'rgba(34,217,127,0.2)',  text: '#22D97F' },
  'Tips & Tricks': { bg: 'rgba(99,102,241,0.08)',  border: 'rgba(99,102,241,0.2)',  text: '#818CF8' },
  'Brand Stories': { bg: 'rgba(245,158,11,0.08)',  border: 'rgba(245,158,11,0.2)',  text: '#FBBF24' },
  'Industry':      { bg: 'rgba(239,68,68,0.08)',   border: 'rgba(239,68,68,0.2)',   text: '#F87171' },
  default:         { bg: 'rgba(0,229,204,0.05)',   border: 'rgba(0,229,204,0.12)',  text: '#00E5CC' },
};

/* ── Blog API helper — reads from /api/public/blogs (no auth required) ── */
async function fetchBlogs({ limit = 6, featured = false } = {}) {
  try {
    const params = new URLSearchParams({ limit, ...(featured ? { featured: 'true' } : {}) });
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api'}/public/blogs?${params}`,
      { headers: { 'Content-Type': 'application/json' } }
    );
    if (!res.ok) throw new Error('Blog fetch failed');
    const json = await res.json();
    // Support many response shapes:
    //   { success: true, data: [...] }
    //   { success: true, data: { blogs: [...] } }
    //   { blogs: [...] }
    //   [...]  (raw array)
    if (Array.isArray(json)) return json;
    if (json.success) {
      return Array.isArray(json.data)
        ? json.data
        : json.data?.blogs || json.data?.posts || [];
    }
    return json.blogs || json.data?.blogs || [];
  } catch {
    return [];
  }
}

/* ── Blog Card component ── */
const BlogCard = ({ post, featured = false }) => {
  const navigate = useNavigate();
  const cat = post.category || 'Authenticity';
  const colors = BLOG_CAT_COLORS[cat] || BLOG_CAT_COLORS.default;

  const formattedDate = post.publishedAt || post.createdAt
    ? new Date(post.publishedAt || post.createdAt).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
      })
    : 'Recently';

  const readTime = post.readTime || Math.max(2, Math.ceil((post.content?.length || 600) / 200)) + ' min read';
  const slug = post.slug || post._id || post.id;

  return (
    <div
      className={`blog-card${featured ? ' blog-card--featured' : ''}`}
      onClick={() => navigate(`/blog/${slug}`)}
      style={{ cursor: 'pointer' }}
    >
      {/* Cover image */}
      <div className="blog-card-img">
        {post.coverImage || post.image ? (
          <img src={post.coverImage || post.image} alt={post.title} />
        ) : (
          <div className="blog-card-img-placeholder">
            <span>{cat === 'Product News' ? '📰' : cat === 'Brand Stories' ? '✨' : cat === 'Tips & Tricks' ? '💡' : '🛡️'}</span>
          </div>
        )}
        {/* Overlay gradient */}
        <div className="blog-card-img-overlay" />
        {/* Category badge on image */}
        <div
          className="blog-card-cat-badge"
          style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.text }}
        >
          {cat}
        </div>
      </div>

      {/* Content */}
      <div className="blog-card-body">
        <h3 className="blog-card-title">{post.title}</h3>
        {post.excerpt && <p className="blog-card-excerpt">{post.excerpt}</p>}

        <div className="blog-card-meta">
          {/* Author */}
          <div className="blog-card-author">
            <div className="blog-card-avatar">
              {post.author?.avatar
                ? <img src={post.author.avatar} alt={post.author?.name || 'Admin'} />
                : <span>{(post.author?.name || 'A')[0].toUpperCase()}</span>}
            </div>
            <div>
              <div className="blog-card-author-name">{post.author?.name || 'JepTags Team'}</div>
              <div className="blog-card-author-role">{post.author?.role || 'Admin'}</div>
            </div>
          </div>

          {/* Date + read time */}
          <div className="blog-card-date-time">
            <span>{formattedDate}</span>
            <span className="blog-card-dot">·</span>
            <span>{readTime}</span>
          </div>
        </div>

        <div className="blog-card-cta">
          Read article
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </div>
      </div>
    </div>
  );
};

/* ── Blog skeleton loader ── */
const BlogSkeleton = () => (
  <div className="blog-card blog-card--skeleton">
    <div className="blog-skel-img" />
    <div className="blog-card-body">
      <div className="blog-skel-line" style={{ width: '30%', height: 12, marginBottom: 10 }} />
      <div className="blog-skel-line" style={{ width: '90%', height: 18, marginBottom: 6 }} />
      <div className="blog-skel-line" style={{ width: '70%', height: 18, marginBottom: 14 }} />
      <div className="blog-skel-line" style={{ width: '100%', height: 12, marginBottom: 4 }} />
      <div className="blog-skel-line" style={{ width: '80%', height: 12, marginBottom: 20 }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="blog-skel-circle" />
          <div className="blog-skel-line" style={{ width: 70, height: 10 }} />
        </div>
        <div className="blog-skel-line" style={{ width: 80, height: 10 }} />
      </div>
    </div>
  </div>
);

/* ── Inline shield SVG for hero phones ── */
const HeroShield = () => {
  const uid = Math.random().toString(36).slice(2,7);
  const g1=`hs-g1-${uid}`, g2=`hs-g2-${uid}`, gw=`hs-gw-${uid}`;
  return (
    <svg width="72" height="72" viewBox="0 0 100 100" fill="none">
      <defs>
        <linearGradient id={g1} x1="20" y1="10" x2="80" y2="90">
          <stop offset="0%" stopColor="#00FFE0"/><stop offset="60%" stopColor="#00D4B8"/><stop offset="100%" stopColor="#00B89C"/>
        </linearGradient>
        <linearGradient id={g2} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#00FFE0"/><stop offset="100%" stopColor="#00C8AA"/>
        </linearGradient>
        <filter id={gw} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="1.8" result="blur"/>
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>
      <rect width="100" height="100" rx="22" fill="#0A1628"/>
      <path d="M50 10 L18 24 L18 52 C18 70 50 88 50 88 C50 88 82 70 82 52 L82 24 Z"
        fill="none" stroke="#00E5CC" strokeWidth="3.5" strokeLinejoin="round" opacity="0.18" filter={`url(#${gw})`}/>
      <path d="M50 10 L18 24 L18 52 C18 70 50 88 50 88 C50 88 82 70 82 52 L82 24 Z"
        fill="none" stroke={`url(#${g1})`} strokeWidth="2.4" strokeLinejoin="round"/>
      <circle cx="50" cy="47" r="20" fill="none" stroke="#00D4B8" strokeWidth="1.4" opacity="0.3"/>
      <path d="M70 47 L73 47" stroke="#00E5CC" strokeWidth="1.2" opacity="0.55"/>
      <text x="50" y="59" textAnchor="middle" fontFamily="Sora,sans-serif" fontWeight="700" fontSize="30"
        fill={`url(#${g2})`} filter={`url(#${gw})`}>J</text>
    </svg>
  );
};

const HeroPhone = ({ mode = 'nfc' }) => {
  const isNfc = mode === 'nfc';
  const uid = Math.random().toString(36).slice(2,7);

  const QrGraphic = () => (
    <svg width="100" height="100" viewBox="0 0 80 80" fill="none">
      <rect x="4" y="4" width="22" height="22" rx="3" stroke="#00E5CC" strokeWidth="2.5"/>
      <rect x="9" y="9" width="12" height="12" rx="1" fill="#00E5CC" opacity="0.7"/>
      <rect x="54" y="4" width="22" height="22" rx="3" stroke="#00E5CC" strokeWidth="2.5"/>
      <rect x="59" y="9" width="12" height="12" rx="1" fill="#00E5CC" opacity="0.7"/>
      <rect x="4" y="54" width="22" height="22" rx="3" stroke="#00E5CC" strokeWidth="2.5"/>
      <rect x="9" y="59" width="12" height="12" rx="1" fill="#00E5CC" opacity="0.7"/>
      <rect x="32" y="32" width="9" height="9" fill="#00E5CC" opacity="0.5"/>
      <rect x="45" y="32" width="5" height="5" fill="#00E5CC" opacity="0.4"/>
      <rect x="32" y="45" width="5" height="5" fill="#00E5CC" opacity="0.4"/>
      <rect x="55" y="55" width="15" height="15" rx="2" fill="#00E5CC" opacity="0.6"/>
    </svg>
  );

  return (
    <div style={{
      width: 260,
      borderRadius: 44,
      background: 'linear-gradient(155deg, #0f2140 0%, #07101f 55%, #060d1a 100%)',
      border: '1.5px solid',
      borderColor: isNfc ? 'rgba(0,229,204,0.2)' : 'rgba(34,217,127,0.15)',
      overflow: 'hidden',
      position: 'relative',
    }}>
      <div style={{position:'absolute',top:0,left:0,right:0,height:'40%',background:'linear-gradient(160deg,rgba(0,229,204,0.06) 0%,transparent 100%)',pointerEvents:'none',zIndex:10,borderRadius:'44px 44px 0 0'}}/>
      <div style={{position:'absolute',top:'10%',bottom:'10%',left:0,width:1,background:'linear-gradient(180deg,transparent,rgba(0,229,204,0.3),transparent)',zIndex:10}}/>
      <div style={{width:80,height:22,background:'#060e1c',borderRadius:'0 0 16px 16px',margin:'0 auto',borderBottom:'1px solid rgba(0,229,204,0.07)',position:'relative',zIndex:11}}/>

      <div style={{padding:'10px 16px 14px',display:'flex',flexDirection:'column',gap:8}}>
        <div style={{textAlign:'center',fontSize:10,fontWeight:700,letterSpacing:'3px',textTransform:'uppercase',color:isNfc?'rgba(0,229,204,0.55)':'rgba(34,217,127,0.55)'}}>
          {isNfc ? 'SCAN NFC CHIP' : 'SCAN QR CODE'}
        </div>

        {isNfc ? (
          <div style={{position:'relative',width:140,height:140,margin:'0 auto',display:'flex',alignItems:'center',justifyContent:'center',overflow:'hidden',borderRadius:'50%'}}>
            <style>{`@keyframes nfcRing_${uid}{0%{transform:scale(0.5);opacity:0.9}100%{transform:scale(1.6);opacity:0}}`}</style>
            <svg style={{position:'absolute',inset:0,width:'100%',height:'100%'}} viewBox="0 0 140 140" fill="none">
              <circle cx="70" cy="70" r="65" stroke="#00E5CC" strokeWidth="0.7" opacity="0.06"/>
              <circle cx="70" cy="70" r="52" stroke="#00E5CC" strokeWidth="0.8" opacity="0.1"/>
              <circle cx="70" cy="70" r="40" stroke="#00E5CC" strokeWidth="1" opacity="0.17"/>
              <circle cx="70" cy="70" r="28" stroke="#00E5CC" strokeWidth="1.2" opacity="0.26"/>
              <circle cx="70" cy="70" r="28" stroke="#00E5CC" strokeWidth="2.5" fill="none" style={{animation:`nfcRing_${uid} 2.6s ease-out infinite`}}/>
              <circle cx="70" cy="70" r="28" stroke="#00E5CC" strokeWidth="2" fill="none" style={{animation:`nfcRing_${uid} 2.6s ease-out 0.85s infinite`}}/>
              <circle cx="70" cy="70" r="28" stroke="#00FFE8" strokeWidth="1.5" fill="none" style={{animation:`nfcRing_${uid} 2.6s ease-out 1.7s infinite`}}/>
            </svg>
            <HeroShield />
          </div>
        ) : (
          <div style={{width:150,height:150,margin:'0 auto',border:'2px solid rgba(0,229,204,0.35)',borderRadius:16,position:'relative',overflow:'hidden',display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(0,229,204,0.018)'}}>
            {[
              {top:0,left:0,borderTop:'3px solid #00E5CC',borderLeft:'3px solid #00E5CC',borderRadius:'14px 0 0 0'},
              {top:0,right:0,borderTop:'3px solid #00E5CC',borderRight:'3px solid #00E5CC',borderRadius:'0 14px 0 0'},
              {bottom:0,left:0,borderBottom:'3px solid #00E5CC',borderLeft:'3px solid #00E5CC',borderRadius:'0 0 0 14px'},
              {bottom:0,right:0,borderBottom:'3px solid #00E5CC',borderRight:'3px solid #00E5CC',borderRadius:'0 0 14px 0'},
            ].map((s,i)=><div key={i} style={{position:'absolute',width:24,height:24,...s}}/>)}
            <style>{`@keyframes qScanMove{0%,100%{top:8%}50%{top:82%}}`}</style>
            <div style={{position:'absolute',left:4,right:4,height:3,background:'linear-gradient(90deg,transparent,#00E5CC,#80fff0,#00E5CC,transparent)',boxShadow:'0 0 16px rgba(0,229,204,0.9), 0 0 32px rgba(0,229,204,0.4)',animation:'qScanMove 1.8s ease-in-out infinite'}}/>
            <QrGraphic />
          </div>
        )}

        <div style={{textAlign:'center',fontSize:10,color:'#00E5CC',fontFamily:'monospace',opacity:0.6,letterSpacing:'0.5px'}}>
          {isNfc ? 'hold phone near tag...' : 'scanning QR code...'}
        </div>

        <div style={{display:'flex',alignItems:'center',gap:10,background:'linear-gradient(135deg,rgba(34,197,94,0.12),rgba(34,197,94,0.04))',border:'1px solid rgba(34,197,94,0.28)',borderRadius:14,padding:'9px 13px',boxShadow:'0 4px 20px rgba(34,197,94,0.1)'}}>
          <div style={{width:26,height:26,borderRadius:'50%',background:'linear-gradient(135deg,#22C55E,#16A34A)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,boxShadow:'0 0 14px rgba(34,197,94,0.5)'}}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round"><path d="M5 12l5 5L19 7"/></svg>
          </div>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:'#22C55E'}}>Authentic Product</div>
            <div style={{fontSize:10,color:'rgba(224,240,240,0.38)'}}>{isNfc?'Tag #JT-00482A · 0.3s':'Tag #HADOTRAN123 · QR'}</div>
          </div>
        </div>

        <div style={{display:'flex',alignItems:'center',gap:10,background:'linear-gradient(135deg,rgba(0,229,204,0.09),rgba(0,229,204,0.03))',border:'1px solid rgba(0,229,204,0.2)',borderRadius:14,padding:'9px 13px'}}>
          <div style={{width:26,height:26,borderRadius:'50%',background:'rgba(0,229,204,0.13)',border:'1.5px solid rgba(0,229,204,0.38)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontSize:13}}>⭐</div>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:'#00E5CC'}}>You Won a Reward!</div>
            <div style={{fontSize:10,color:'rgba(224,240,240,0.38)'}}>{isNfc?'Discount coupon unlocked':'Spin to win prizes'}</div>
          </div>
        </div>

        <div style={{display:'flex',justifyContent:'space-around',borderTop:'1px solid rgba(0,229,204,0.07)',paddingTop:11,marginTop:1}}>
          {[
            {active:isNfc,label:'NFC',icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 8.32a7.43 7.43 0 010 7.36"/><path d="M9.46 6.21a11.76 11.76 0 010 11.58"/><path d="M12.91 4.1a16.1 16.1 0 010 15.8"/></svg>},
            {active:!isNfc,label:'QR',icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="4" height="4"/></svg>},
          ].map(n=>(
            <div key={n.label} style={{display:'flex',flexDirection:'column',alignItems:'center',gap:3,fontSize:9,fontWeight:700,letterSpacing:'1.5px',textTransform:'uppercase',color:n.active?'#00E5CC':'rgba(224,240,240,0.18)'}}>
              {n.icon}{n.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};


export default function HomePage({ onAdd }) {
  const [data, setData] = useState({ featured:[], popular:[], newest:[], local:[], global:[], brands:[] });
  const [productBrands, setProductBrands] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('featured');
  const [userCountry, setUserCountry] = useState({ country: 'US', currency: 'USD', countryName: 'United States' });
  const [countryReady, setCountryReady] = useState(false);
  const [adding, setAdding] = useState(null);

  /* ── BLOG STATE ── */
  const [blogs, setBlogs] = useState([]);
  const [blogsLoading, setBlogsLoading] = useState(true);
  const [blogFilter, setBlogFilter] = useState('All');

  const particlesRef = useRef(null);
  const navigate = useNavigate();

  /* ── Country detection ── */
  useEffect(() => {
    detectCountry().then(info => {
      setUserCountry(info);
      setCountryReady(true);
    });
  }, []);

  /* ── Particles ── */
  useEffect(() => {
    const el = particlesRef.current; if (!el) return;
    for (let i=0;i<25;i++){
      const p = document.createElement('div');
      p.style.cssText = `position:absolute;border-radius:50%;background:#00E5CC;opacity:0;
        width:${1+Math.random()*2}px;height:${1+Math.random()*2}px;
        left:${Math.random()*100}%;
        animation:pRise ${6+Math.random()*8}s linear ${Math.random()*10}s infinite`;
      el.appendChild(p);
    }
    return () => { el.innerHTML = ''; };
  }, []);

  /* ── Products ── */
  useEffect(() => {
    if (!countryReady) return;
    Promise.all([
      shopApi.getFeatured().catch(() => ({ success:false })),
      shopApi.getProducts({ country: userCountry.country, limit: 12, sort: 'newest', localOnly: 'true' }).catch(() => ({ success:false, data:{ products:[] } })),
      shopApi.getProducts({ country: userCountry.country, limit: 12, sort: 'newest', globalOnly: 'true' }).catch(() => ({ success:false, data:{ products:[] } })),
      shopApi.getCategories().catch(() => ({ success:false })),
      shopApi.getProductBrands({ limit: 8 }).catch(() => ({ success:false })),
    ]).then(([feat, loc, glob, c, pb]) => {
      if(feat.success) setData({ ...feat.data, local: loc.success ? (loc.data?.products||[]) : [], global: glob?.success ? (glob.data?.products||[]) : [] });
      if (c.success) setCats(c.data.categories || []);
      if (pb.success) setProductBrands(pb.data.brands || []);
      setLoading(false);
    });
  }, [countryReady]);

  /* ── Blogs — fetched from admin /api/blogs ── */
  useEffect(() => {
    setBlogsLoading(true);
    fetchBlogs({ limit: 6 })
      .then(posts => setBlogs(posts))
      .finally(() => setBlogsLoading(false));
  }, []);

  const handleAdd = async (product) => {
    const id = product.productId || String(product._id);
    setAdding(id);
    await onAdd(product.productId || product._id, 1, null, userCountry.country || '');
    setTimeout(() => setAdding(null), 1200);
  };

  const tabItems = tab === 'local' ? data.local : tab === 'global' ? data.global : tab === 'popular' ? data.popular : tab === 'newest' ? data.newest : data.featured;

  /* Blog category filter */
  const blogCategories = ['All', ...Array.from(new Set(blogs.map(b => b.category).filter(Boolean)))];
  const filteredBlogs = blogFilter === 'All' ? blogs : blogs.filter(b => b.category === blogFilter);

  return (
    <div>
      <style>{`
        @keyframes pRise{0%{transform:translateY(100vh) scale(0);opacity:0}10%{opacity:.5}90%{opacity:.5}100%{transform:translateY(-10vh) scale(1);opacity:0}}
        @keyframes qScan2{0%,100%{top:8%}50%{top:82%}}
        @keyframes blogCardIn{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
        @keyframes blogSkelShimmer{0%{background-position:-400px 0}100%{background-position:400px 0}}
      `}</style>

      {/* HERO */}
      <section className="shop-hero">
        <div className="hero-grid" />
        <div className="hero-orb o1" /><div className="hero-orb o2" /><div className="hero-orb o3" />
        <div ref={particlesRef} style={{position:'absolute',inset:0,overflow:'hidden',pointerEvents:'none'}} />

        <div className="hero-inner">
          <div className="hero-left">
            <div className="hero-badge"><div className="badge-dot" />Certified Authentic Products</div>
            <h1 className="hero-h">Shop <span className="grad">Genuine</span><br />Products Only</h1>
            <p className="hero-p">JepTags guarantees every product is 100% authentic. Scan the tag on any item to instantly verify it's the real thing — before you buy or after.</p>
            <div className="hero-btns">
              <button className="btn-primary" onClick={() => navigate('/products')}>Shop Now →</button>
              <button className="btn-outline" onClick={() => navigate('/brands')}>Browse Brands</button>
            </div>
            <div className="hero-stats">
              <div className="hero-stat"><div className="v">100%</div><div className="l">Authentic</div></div>
              <div className="hero-stat"><div className="v">0</div><div className="l">Fakes</div></div>
              <div className="hero-stat"><div className="v">Fast</div><div className="l">Delivery</div></div>
            </div>
          </div>

          <div style={{display:'flex',gap:32,alignItems:'center',perspective:'900px',perspectiveOrigin:'60% 50%'}}>
            <div style={{transform:'rotateY(22deg) rotateX(6deg) translateY(-24px) scale(1)',transformStyle:'preserve-3d',transition:'transform 0.6s cubic-bezier(0.34,1.56,0.64,1)',filter:'drop-shadow(-24px 48px 64px rgba(0,229,204,0.35)) drop-shadow(0 64px 100px rgba(0,0,0,0.8))',cursor:'pointer'}}
              onMouseEnter={e=>{e.currentTarget.style.transform='rotateY(5deg) rotateX(2deg) translateY(-24px) scale(1.06)';e.currentTarget.style.filter='drop-shadow(-8px 32px 48px rgba(0,229,204,0.55)) drop-shadow(0 40px 80px rgba(0,0,0,0.6))'}}
              onMouseLeave={e=>{e.currentTarget.style.transform='rotateY(22deg) rotateX(6deg) translateY(-24px) scale(1)';e.currentTarget.style.filter='drop-shadow(-24px 48px 64px rgba(0,229,204,0.35)) drop-shadow(0 64px 100px rgba(0,0,0,0.8))'}}>
              <div style={{textAlign:'center',marginBottom:14,fontSize:11,fontWeight:800,letterSpacing:'2.5px',textTransform:'uppercase',color:'rgba(0,229,204,0.8)',display:'flex',alignItems:'center',justifyContent:'center',gap:7}}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M6 8.32a7.43 7.43 0 010 7.36"/><path d="M9.46 6.21a11.76 11.76 0 010 11.58"/><path d="M12.91 4.1a16.1 16.1 0 010 15.8"/></svg>
                NFC TAP
              </div>
              <HeroPhone mode="nfc" />
            </div>

            <div style={{transform:'rotateY(-16deg) rotateX(6deg) translateY(24px) scale(1)',transformStyle:'preserve-3d',transition:'transform 0.6s cubic-bezier(0.34,1.56,0.64,1)',filter:'drop-shadow(24px 48px 64px rgba(34,217,127,0.28)) drop-shadow(0 64px 100px rgba(0,0,0,0.8))',cursor:'pointer'}}
              onMouseEnter={e=>{e.currentTarget.style.transform='rotateY(-4deg) rotateX(2deg) translateY(24px) scale(1.06)';e.currentTarget.style.filter='drop-shadow(8px 32px 48px rgba(34,217,127,0.5)) drop-shadow(0 40px 80px rgba(0,0,0,0.6))'}}
              onMouseLeave={e=>{e.currentTarget.style.transform='rotateY(-16deg) rotateX(6deg) translateY(24px) scale(1)';e.currentTarget.style.filter='drop-shadow(24px 48px 64px rgba(34,217,127,0.28)) drop-shadow(0 64px 100px rgba(0,0,0,0.8))'}}>
              <div style={{textAlign:'center',marginBottom:14,fontSize:11,fontWeight:800,letterSpacing:'2.5px',textTransform:'uppercase',color:'rgba(34,217,127,0.8)',display:'flex',alignItems:'center',justifyContent:'center',gap:7}}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="4" height="4"/></svg>
                QR SCAN
              </div>
              <HeroPhone mode="qr" />
            </div>
          </div>
        </div>
      </section>

      {/* FLASH STRIP */}
      <div className="flash-strip">
        <div className="flash-label">🔥 Today's Deals</div>
        <div className="flash-items">
          <span className="flash-item">Free shipping on orders <strong>over $50</strong></span>
          <span className="flash-item">•</span>
          <span className="flash-item">Every product is <strong>100% authentic</strong></span>
          <span className="flash-item">•</span>
          <span className="flash-item"><strong>30-day</strong> hassle-free returns</span>
        </div>
      </div>

      {/* TRUST BAR */}
      <div className="trust-bar">
        <div className="trust-bar-inner">
          {[
            {icon:'✅',title:'Authenticity Guaranteed',desc:'Every product is verified genuine'},
            {icon:'🚚',title:'Free Shipping',desc:'On all orders over $50'},
            {icon:'↩️',title:'30-Day Returns',desc:'Hassle-free return policy'},
            {icon:'🛡️',title:'Secure Payments',desc:'100% safe & protected'},
          ].map(t => (
            <div key={t.title} className="trust-item">
              <div className="trust-icon">{t.icon}</div>
              <div>
                <div className="trust-title">{t.title}</div>
                <div className="trust-desc">{t.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="page-wrap">

        {/* CATEGORIES */}
        {cats.length > 0 && (
          <div className="home-section">
            <div className="sec-row">
              <div className="sec-hdr" style={{textAlign:'left',marginBottom:0}}>
                <h2>Shop by <span className="grad">Category</span></h2>
                <p>Find exactly what you're looking for</p>
              </div>
            </div>
            <div style={{marginTop:28}} />
            <div className="categories-grid">
              {cats.slice(0,8).map(c => (
                <Link key={c.category} to={`/products?category=${encodeURIComponent(c.category)}`} className="cat-card">
                  <div className="cat-icon">{CAT_ICONS[c.category]||CAT_ICONS.default}</div>
                  <div className="cat-name">{c.category}</div>
                  <div className="cat-count">{c.count} {c.count === 1 ? "item" : "items"}</div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* PRODUCTS */}
        <div className="home-section">
          <div className="sec-row">
            <div className="sec-hdr" style={{textAlign:'left',marginBottom:0}}>
              <h2>Discover <span className="grad">Products</span></h2>
              <p>Handpicked from verified sellers &nbsp;·&nbsp;
                <span style={{color:'var(--cyan)',fontWeight:700,fontSize:13}}>
                  📍 {userCountry.countryName}
                </span>
              </p>
            </div>
            <Link to="/products" className="see-all">View all →</Link>
          </div>

          <div className="sec-tabs">
            {[['local',`📍 ${countryReady && userCountry.countryName ? userCountry.countryName : 'Local'}`],['global','🌐 Global'],['featured','⭐ Featured'],['newest','🆕 New Arrivals'],['popular','🔥 Best Sellers']].map(([v,l]) => (
              <button key={v} className={`sec-tab ${tab===v?'active':''}`} onClick={() => setTab(v)}>{l}</button>
            ))}
          </div>

          {loading ? <div className="spinner" /> : (
            tabItems.length ? (
              <div className="products-grid">
                {tabItems.slice(0,10).map(p => (
                  <ProductCard key={p.productId || (p._id?.__toString?.() || String(p._id))} product={p}
                    onAdd={handleAdd} adding={adding===(p.productId || String(p._id))}
                    currency={userCountry.currency} />
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <div className="icon">📦</div>
                <h3>No products yet</h3>
                <p>Add products from the business dashboard to see them here.</p>
              </div>
            )
          )}

          {tabItems.length > 0 && (
            <div style={{textAlign:'center',marginTop:36}}>
              <button className="btn-primary" style={{padding:'13px 36px',fontSize:'15px'}} onClick={() => navigate('/products')}>
                View All Products →
              </button>
            </div>
          )}
        </div>

        {/* BRANDS */}
        {productBrands.length > 0 && (
          <div className="home-section">
            <div className="sec-row">
              <div className="sec-hdr" style={{textAlign:'left',marginBottom:0}}>
                <h2>Top <span className="grad">Brands</span></h2>
                <p>Shop directly from verified manufacturers</p>
              </div>
              <Link to="/brands" className="see-all">All brands →</Link>
            </div>
            <div style={{marginTop:28}} />
            <div className="brands-grid">
              {productBrands.map(b => (
                <Link to={`/products?brand=${encodeURIComponent(b.brandName)}`} key={b.brandName} className="brand-chip">
                  <div className="brand-chip-logo">
                    {b.imageUrl ? <img src={b.imageUrl} alt={b.brandName} /> : '🏷️'}
                  </div>
                  <div>
                    <div className="brand-chip-name">{b.brandName}</div>
                    <div className="brand-chip-count">{b.productCount} {b.productCount === 1 ? 'product' : 'products'}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════
            BLOG SECTION — admin-controlled posts
            ════════════════════════════════════════════════ */}
        {(blogsLoading || blogs.length > 0) && (
          <div className="home-section blog-section">

            {/* Section header */}
            <div className="sec-row blog-section-header">
              <div className="sec-hdr" style={{textAlign:'left',marginBottom:0}}>
                <div className="blog-section-label">
                  <span className="blog-label-dot" />
                  From the Admin
                </div>
                <h2>Latest <span className="grad">News &amp; Insights</span></h2>
                <p>Updates, guides, and stories from the JepTags team</p>
              </div>
              <Link to="/blog" className="see-all">All articles →</Link>
            </div>

            {/* Category filter pills */}
            {!blogsLoading && blogCategories.length > 1 && (
              <div className="blog-filter-pills">
                {blogCategories.map(cat => {
                  const colors = cat === 'All'
                    ? { bg: 'rgba(0,229,204,0.08)', border: 'rgba(0,229,204,0.2)', text: '#00E5CC' }
                    : (BLOG_CAT_COLORS[cat] || BLOG_CAT_COLORS.default);
                  const isActive = blogFilter === cat;
                  return (
                    <button
                      key={cat}
                      className={`blog-filter-pill${isActive ? ' active' : ''}`}
                      style={isActive ? { background: colors.bg, borderColor: colors.border, color: colors.text } : {}}
                      onClick={() => setBlogFilter(cat)}
                    >
                      {cat}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Grid */}
            <div className="blog-grid">
              {blogsLoading
                ? Array.from({ length: 3 }).map((_, i) => <BlogSkeleton key={i} />)
                : filteredBlogs.length > 0
                  ? filteredBlogs.slice(0, 6).map((post, i) => (
                      <div
                        key={post._id || post.id || i}
                        style={{ animation: `blogCardIn 0.5s ease-out ${i * 0.08}s both` }}
                      >
                        <BlogCard post={post} featured={i === 0} />
                      </div>
                    ))
                  : (
                    <div className="blog-empty">
                      <span className="blog-empty-icon">✍️</span>
                      <p>No articles in this category yet.</p>
                      <button className="blog-filter-pill" onClick={() => setBlogFilter('All')}>
                        Show all articles
                      </button>
                    </div>
                  )
              }
            </div>

            {/* View all CTA */}
            {!blogsLoading && blogs.length > 0 && (
              <div style={{textAlign:'center',marginTop:36}}>
                <button
                  className="btn-outline"
                  style={{padding:'12px 34px',fontSize:'14px'}}
                  onClick={() => navigate('/blog')}
                >
                  Read All Articles →
                </button>
              </div>
            )}
          </div>
        )}
        {/* ════════ END BLOG SECTION ════════ */}

        {/* AUTHENTICITY GUIDE */}
        <AuthenticityGuide />

        {/* CTA BANNER */}
        <div className="home-section">
          <div className="home-cta-banner">
            <h2>Are you a brand? <span className="grad">Sell on JepTags</span></h2>
            <p>List your authentic products and reach customers who care about genuineness.</p>
            <div className="cta-btns">
              <button className="btn-primary" style={{padding:'13px 32px'}} onClick={() => navigate('/business/register')}>
                Register Business →
              </button>
              <button className="btn-outline" style={{padding:'13px 32px'}} onClick={() => navigate('/business/login')}>
                Sign In
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}